import './hero.scss';

import ArrowNextIcon from '@frontend/images/icons/ArrowNextIcon';
import heroOverlay4 from '@frontend/images/bgHero4.svg';
import heroOverlay3 from '@frontend/images/bgHero3.svg';
import heroOverlay2 from '@frontend/images/bgHero2.svg';
import heroOverlay1 from '@frontend/images/bgHero1.svg';
import { useEffect, useRef, useState } from 'react';
import heroBase from '@frontend/images/bgHero.svg';
import useT from '@frontend/js/hooks/useT';


const heroLayers = [heroOverlay1, heroOverlay4, heroOverlay3, heroOverlay2];
const layerChangeInterval = 2800;
const statCounterDuration = 1900;

function easeInOutQuart(progress) {
    return progress < 0.5
        ? 8 * Math.pow(progress, 4)
        : 1 - Math.pow(-2 * progress + 2, 4) / 2;
}

function parseNumberParts(rawValue) {
    const value = `${rawValue ?? ''}`;
    const match = value.match(/(\d(?:[\d\s.,]*\d)?)/);

    if (!match || match.index === undefined) {
        return null;
    }

    const numericPart = match[1];
    const normalized = numericPart.replace(/\s+/g, '').replace(',', '.');
    const target = Number.parseFloat(normalized);

    if (Number.isNaN(target)) {
        return null;
    }

    const decimalMatch = normalized.match(/\.(\d+)$/);

    return {
        prefix: value.slice(0, match.index),
        suffix: value.slice(match.index + numericPart.length),
        target,
        decimals: decimalMatch ? decimalMatch[1].length : 0,
    };
}

function formatAnimatedValue(parsed, progress) {
    if (!parsed) {
        return '';
    }

    const current = parsed.target * progress;
    const numberText = parsed.decimals > 0
        ? current.toFixed(parsed.decimals)
        : String(Math.round(current));

    return `${parsed.prefix}${numberText}${parsed.suffix}`;
}

function splitDisplayValue(rawValue) {
    const value = `${rawValue ?? ''}`;
    const match = value.match(/(\d(?:[\d\s.,]*\d)?)/);

    if (!match || match.index === undefined) {
        return {
            prefix: '',
            number: value,
            suffix: '',
        };
    }

    const number = match[1];

    return {
        prefix: value.slice(0, match.index),
        number,
        suffix: value.slice(match.index + number.length),
    };
}



export default function Hero({ onCtaClick }) {
    const [activeLayer, setActiveLayer] = useState(0);
    const [animatedStats, setAnimatedStats] = useState([]);
    const [hasStatsBeenVisible, setHasStatsBeenVisible] = useState(false);
    const statsRef = useRef(null);
    const { t } = useT('site');

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setActiveLayer((currentLayer) => (currentLayer + 1) % heroLayers.length);
        }, layerChangeInterval);

        return () => window.clearInterval(intervalId);
    }, []);

    const stats = [
        {
            number: t('home-banner-statistics-years-number', ''),
            text: t('home-banner-statistics-years-text', ''),
        },
        {
            number: t('home-banner-statistics-projects-number', ''),
            text: t('home-banner-statistics-projects-text', ''),
        },
        {
            number: t('home-banner-statistics-clients-number', ''),
            text: t('home-banner-statistics-clients-text', ''),
        },
    ].filter((item) => item.number || item.text);

    const statsSignature = stats
        .map((stat) => `${stat?.number || ''}|${stat?.text || ''}`)
        .join('||');

    useEffect(() => {
        if (hasStatsBeenVisible || !stats.length) {
            return;
        }

        const node = statsRef.current;

        if (!node) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setHasStatsBeenVisible(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.3,
            }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [hasStatsBeenVisible, stats.length]);

    useEffect(() => {
        if (!hasStatsBeenVisible) {
            return;
        }

        if (!Array.isArray(stats) || stats.length === 0) {
            setAnimatedStats([]);
            return;
        }

        const parsedStats = stats.map((stat) => parseNumberParts(stat?.number));
        const hasNumbers = parsedStats.some(Boolean);

        if (!hasNumbers) {
            setAnimatedStats(stats.map((stat) => stat?.number || ''));
            return;
        }

        let frameId = null;
        const startTime = performance.now();

        const tick = (now) => {
            const linearProgress = Math.min((now - startTime) / statCounterDuration, 1);
            const easedProgress = easeInOutQuart(linearProgress);

            setAnimatedStats(
                stats.map((stat, index) => {
                    const parsed = parsedStats[index];

                    if (!parsed) {
                        return stat?.number || '';
                    }

                    return formatAnimatedValue(parsed, easedProgress);
                })
            );

            if (linearProgress < 1) {
                frameId = window.requestAnimationFrame(tick);
            }
        };

        frameId = window.requestAnimationFrame(tick);

        return () => {
            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, [hasStatsBeenVisible, statsSignature]);

    return (
        <div className="hero">
            <div className="hero__background" aria-hidden="true">
                <img className="hero__background-base" src={heroBase} alt="" />
                {heroLayers.map((layer, index) => (
                    <img
                        key={layer}
                        className={`hero__background-layer ${activeLayer === index ? 'is-active' : ''}`}
                        src={layer}
                        alt=""
                    />
                ))}
            </div>
            <div className="hero__content">
            {t('home-banner-title', '') && <h1>{t('home-banner-title', '')}</h1>}
                <div className="hero__content__subtitle">
                    {t('home-banner-text', '') && <h3>{t('home-banner-text', '')}</h3>}
                    {t('home-banner-button', '') && (
                             <button type="button" className="hero__content__subtitle__button" onClick={onCtaClick}>{t('home-banner-button', '')} <ArrowNextIcon className="hero__content__subtitle__button__icon" /> </button>
                    )}
                </div>

                {stats.length > 0 && (
                    <div ref={statsRef} className="hero__content__stats">
                        {stats.map((stat, i) => {
                            const value = animatedStats[i] || stat.number;
                            const parts = splitDisplayValue(value);
                            const normalizedPrefix = (parts.prefix || '').replace(/\s+$/, '');
                            const normalizedSuffix = (parts.suffix || '').replace(/^\s+/, '');
                            const prefixNeedsGap = normalizedPrefix && /[^\d\s]$/.test(normalizedPrefix);
                            const suffixNeedsGap = normalizedSuffix && /^[^\d\s]/.test(normalizedSuffix);

                            return (
                                <div key={i} className="hero__content__stats__stat">
                                    <h2 className="hero__content__stats__stat__number">
                                        {normalizedPrefix && <span>{normalizedPrefix}</span>}
                                        {prefixNeedsGap && <span>{'\u00A0'}</span>}
                                        <span>{parts.number}</span>
                                        {suffixNeedsGap && <span>{'\u00A0'}</span>}
                                        {normalizedSuffix && <span>{normalizedSuffix}</span>}
                                    </h2>
                                    <p >{stat.text}</p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
